// apps/admin/src/lib/ws-system-channel.ts
//
// Phase 8.3 Wave 5 Task 65 — `useSystemChannel` SA-only WS subscriber.
//
// Responsibilities (per CONTEXT):
//   1. RBAC gate — only render the subscription when `user.role === SA`.
//      Non-SA renders short-circuit; the BE also enforces 403 on
//      subscribe, but we avoid even sending the op.
//   2. Multiplex over the shared `wsClient` connection — subscribe to
//      every documented `system.*` event class. `wsClient` delegates to
//      the canonical `connectWs` (@printway/ui) and maps each event name
//      to its canonical server channel (`ws:system` / `ws:admin`) —
//      reconnect / heartbeat / jittered backoff are inherited from it
//      (dbg-20260703-020244-1493).
//   3. Route each frame into:
//        - `useSystemEventStore.pushEvent` (badge counters + ring
//          buffer used by the SystemEventListener banner)
//        - `useNotificationStore.pushNotification` (bell dropdown +
//          severity-aware sound —)
//        - `useToast` for warning/critical severities (immediate UX)
//   4. Reconnect resume — `useSystemEventStore.lastEventId` is exposed
//      so a future BE patch can include it on the subscribe frame
//      (`{ type:'subscribe', channel:'ws:system', resume_after }`). For
//      now we just record it; the BE replay channel is a Phase 9 item.
//   5. Memory safety — every effect returns an unsubscriber; the WS
//      client decrements its channel ref-count on the last unsub.
//
// Wave-mate hand-off: SystemEventListener mounts this hook globally in
// the admin AppShell so every page receives SA notifications regardless
// of route.
//
// vg-binding: CONTEXT:decisions=P8.D-212,P8.D-260
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useEffect, useMemo } from 'react';
import { useToast } from '@printway/ui';

import { subscribe as wsSubscribe, type WsFrame } from './wsClient.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import {
  useSystemEventStore,
  type SystemEvent,
  type SystemEventKind,
  type SystemEventSeverity,
} from '../stores/system-events.store.js';
import {
  useNotificationStore,
  type AdminNotification,
} from '../stores/useNotificationStore.js';

// ---------------------------------------------------------------------------
// Role gate — must align with useAuthStore.hasRole() semantics
// ---------------------------------------------------------------------------

/**
 * Roles allowed to subscribe to `system.*`. only
 * Super Admin sees raw system events. We accept three common spellings
 * because historical fixtures + envelopes drift between
 * `super_admin` / `superadmin` / `SuperAdmin`; BE remains the final
 * authority via the subscribe-time JWT role check.
 */
const SA_ROLES = ['super_admin', 'superadmin', 'SuperAdmin'] as const;

function isSuperAdminUser(
  user: ReturnType<typeof useAuthStore.getState>['user'],
): boolean {
  if (!user) return false;
  if (SA_ROLES.includes(user.primaryRole as (typeof SA_ROLES)[number])) {
    return true;
  }
  if (SA_ROLES.includes(user.activeRoleSlot as (typeof SA_ROLES)[number])) {
    return true;
  }
  if (Array.isArray(user.roles)) {
    for (const r of user.roles) {
      if (SA_ROLES.includes(r as (typeof SA_ROLES)[number])) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// WS channel set (schema)
// ---------------------------------------------------------------------------

/**
 * Domain event names — the shared wsClient dispatches per server
 * `eventName` and maps each to the canonical RBAC channel it rides on
 * (dbg-20260703-020244-1493):
 *   - `ws:admin`  ← `system.maintenance.changed`
 *   - `ws:system` ← `system.emergency.changed`, `system.announcement.new`,
 *                   `system.health.alert`, `approval.expired` (SA-only).
 */
const SYSTEM_EVENTS: ReadonlyArray<{ event: string; kind: SystemEventKind }> = [
  { event: 'system.health.alert', kind: 'system.health.alert' },
  { event: 'system.maintenance.changed', kind: 'system.maintenance.changed' },
  { event: 'system.emergency.changed', kind: 'system.emergency.changed' },
  { event: 'system.announcement.new', kind: 'system.announcement.new' },
  { event: 'approval.expired', kind: 'approval.expired' },
];

// ---------------------------------------------------------------------------
// Frame normaliser — tolerates partial BE payloads
// ---------------------------------------------------------------------------

interface IncomingPayload {
  id?: string;
  event_id?: string;
  title?: string;
  message?: string;
  description?: string;
  severity?: string;
  href?: string;
  link?: string;
  timestamp?: string;
  received_at?: string;
}

function normaliseSeverity(value: unknown): SystemEventSeverity {
  if (value === 'critical' || value === 'warning' || value === 'info') {
    return value;
  }
  // Heuristic — health.alert defaults to warning, emergency.changed to
  // critical. Caller can override via the kind-specific mapper below.
  return 'info';
}

function pickString(...candidates: Array<unknown>): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c;
  }
  return undefined;
}

function defaultSeverityFor(kind: SystemEventKind): SystemEventSeverity {
  switch (kind) {
    case 'system.health.alert':
    case 'system.emergency.changed':
      return 'critical';
    case 'system.maintenance.changed':
    case 'approval.expired':
      return 'warning';
    case 'system.announcement.new':
      return 'info';
    default:
      return 'info';
  }
}

function defaultTitleFor(kind: SystemEventKind): string {
  switch (kind) {
    case 'system.health.alert':
      return 'System health alert';
    case 'system.maintenance.changed':
      return 'Maintenance window updated';
    case 'system.emergency.changed':
      return 'Emergency mode changed';
    case 'system.announcement.new':
      return 'New system announcement';
    case 'approval.expired':
      return 'Approval expired';
    default:
      return 'System event';
  }
}

function toSystemEvent(frame: WsFrame, kind: SystemEventKind): SystemEvent {
  const raw =
    frame.payload && typeof frame.payload === 'object'
      ? (frame.payload as IncomingPayload)
      : {};
  const id =
    pickString(raw.id, raw.event_id) ??
    `sys_${kind}_${frame.timestamp ?? Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  const severity = raw.severity
    ? normaliseSeverity(raw.severity)
    : defaultSeverityFor(kind);
  const title = pickString(raw.title) ?? defaultTitleFor(kind);
  const description = pickString(raw.message, raw.description);
  const href = pickString(raw.href, raw.link);
  const receivedAt =
    pickString(raw.received_at, raw.timestamp) ??
    (frame.timestamp
      ? new Date(frame.timestamp).toISOString()
      : new Date().toISOString());

  return {
    id,
    kind,
    severity,
    title,
    ...(description !== undefined ? { description } : {}),
    ...(href !== undefined ? { href } : {}),
    receivedAt,
    payload: frame.payload,
  };
}

// ---------------------------------------------------------------------------
// Side-effect mappers
// ---------------------------------------------------------------------------

function toToastVariant(
  severity: SystemEventSeverity,
): 'default' | 'success' | 'error' | 'warning' {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'default';
}

function toAdminNotification(event: SystemEvent): AdminNotification {
  const base: AdminNotification = {
    id: event.id,
    category: 'system',
    title: event.title,
    body: event.description ?? '',
    severity: event.severity === 'critical' ? 'critical' : 'normal',
    isRead: false,
    createdAt: event.receivedAt,
  };
  if (event.href !== undefined) base.href = event.href;
  return base;
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

export interface UseSystemChannelResult {
  /** True when the current user is allowed to receive system.* events. */
  enabled: boolean;
  /** Live counter map — pass-through for caller-side banner rendering. */
  counters: ReturnType<typeof useSystemEventStore.getState>['counters'];
  /** Most-recent event id received — exposed for resume diagnostics. */
  lastEventId: string | null;
}

/**
 * Mount this once at the app shell (see SystemEventListener.tsx). Calling
 * it from a leaf component is also safe — additional mounts share the
 * channel via the wsClient ref-count.
 *
 * Behaviour:
 *   - Non-SA renders: returns `{ enabled:false }` and does NOT subscribe.
 *     This avoids surfacing the BE 403 noise while still keeping the
 *     hook tree-shape consistent for SA-only.
 *   - SA renders: subscribes to every channel in SYSTEM_CHANNELS,
 *     fans out incoming frames into the system-events store + the
 *     notification bell + a severity-aware toast.
 *   - On user-change (impersonation, logout, role swap), the effect's
 *     dep list re-runs, tearing down old subscriptions before
 *     re-subscribing under the new principal.
 */
export function useSystemChannel(): UseSystemChannelResult {
  const user = useAuthStore((s) => s.user);
  const isSA = useMemo(() => isSuperAdminUser(user), [user]);
  const { toast } = useToast();

  // Re-render-safe selectors — wider reactivity is fine, the parent
  // banner is a single component near the shell root.
  const counters = useSystemEventStore((s) => s.counters);
  const lastEventId = useSystemEventStore((s) => s.lastEventId);

  useEffect(() => {
    if (!isSA) return undefined;

    // wsClient.subscribe returns an unsubscribe per event.
    const offFns: Array<() => void> = [];

    for (const { event, kind } of SYSTEM_EVENTS) {
      const off = wsSubscribe(event, (frame) => {
        const event = toSystemEvent(frame, kind);

        // Store first — guarantees badge counter mutation before any
        // downstream consumer (Bell / banner) reads.
        useSystemEventStore.getState().pushEvent(event);

        // Notification bell — system category + severity preserved.
        try {
          useNotificationStore.getState().pushNotification(toAdminNotification(event));
        } catch {
          /* bell mutation is best-effort; failures must not break WS */
        }

        // Toast — only surface warning/critical to avoid noise from
        // routine announcement.new pings (banner counter still bumps).
        if (event.severity === 'warning' || event.severity === 'critical') {
          try {
            const input: Parameters<typeof toast>[0] = {
              title: event.title,
              variant: toToastVariant(event.severity),
              duration: event.severity === 'critical' ? 8000 : 5000,
            };
            if (event.description !== undefined) {
              input.description = event.description;
            }
            toast(input);
          } catch {
            /* toast outside provider — silently drop */
          }
        }
      });
      offFns.push(off);
    }

    return () => {
      for (const off of offFns) {
        try {
          off();
        } catch {
          /* unsubscribe must never throw */
        }
      }
    };
  }, [isSA, toast]);

  return {
    enabled: isSA,
    counters,
    lastEventId,
  };
}

export default useSystemChannel;
